/**
 * 设备检测工具函数
 */

/**
 * 获取用户代理字符串
 * @returns {string} 用户代理字符串
 */
export const getUserAgent = () => {
  return window.navigator.userAgent || ''
}

/**
 * 检查是否为鸿蒙设备
 * @returns {boolean} 是否为鸿蒙设备
 */
export const isHarmonyOS = () => {
  const ua = getUserAgent()

  return /HarmonyOS|OpenHarmony|HONOR/i.test(ua)
}

export const hasAndroidUA = () => {
  const ua = getUserAgent()
  return /Android|Adr/i.test(ua)
}

/**
 * 获取设备平台
 * @returns {string} 'android'|'ios'|'web'
 */
export const getPlatform = () => {
  const ua = getUserAgent()
  if (isHarmonyOS()) {
    return 'harmony'
  }
  if (ua.indexOf('Android') > -1 || ua.indexOf('Adr') > -1) {
    return 'android'
  } else if (ua.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/)) {
    return 'ios'
  } else {
    return 'web'
  }
}

/**
 * 检查是否为iPad
 * @returns {boolean} 是否为iPad
 */
export const isIPad = () => {
  const ua = getUserAgent()

  // 检查用户代理是否直接包含iPad标识
  if (ua.indexOf('iPad') > -1) {
    return true
  }

  // 针对iOS 13+的iPad检测 (iPadOS的UA会像Mac OS一样)
  // iPad在iOS 13后用户代理会变成 Macintosh
  const isIOS = /Macintosh/.test(ua)
  if (isIOS && window.navigator.maxTouchPoints > 1) {
    return true
  }

  // 已知设备型号判断（从项目中获取的方法）
  // if (window.alita && window.alita.getNativeInfo) {
  //   try {
  //     // 异步检查，需要回调方式使用结果
  //     return window.alita.getNativeInfo().then((res) => {
  //       return res.model === 'ipad'
  //     })
  //   } catch (e) {
  //     console.error('获取设备信息失败', e)
  //   }
  // }

  return false
}

/**
 * 检查是否为安卓平板
 * @returns {boolean} 是否为安卓平板
 */
export const isAndroidPad = () => {
  const ua = getUserAgent()

  // 检查是否为安卓设备
  const isAndroid = ua.indexOf('Android') > -1 || ua.indexOf('Adr') > -1

  if (!isAndroid) return false

  // 判断屏幕尺寸
  // 通常认为大于7英寸或分辨率较高的为平板设备
  const screenWidth = window.screen.width
  const screenHeight = window.screen.height
  const screenDiagonal = Math.sqrt(screenWidth * screenWidth + screenHeight * screenHeight)
  const dpr = window.devicePixelRatio || 1

  // 1. 屏幕尺寸判断: 通常平板设备对角线像素数会比较大
  const isBigScreen = screenDiagonal > 1200

  // 2. 检查UA中是否包含Tablet关键字
  const hasTabletInUA = ua.indexOf('Tablet') > -1 || ua.indexOf('tablet') > -1

  // 3. 已知设备型号判断（从项目中获取的方法）
  // if (window.alita && window.alita.getNativeInfo) {
  //   try {
  //     // 异步检查，需要回调方式使用结果
  //     return window.alita.getNativeInfo().then((res) => {
  //       return res.model === 'android_hd'
  //     })
  //   } catch (e) {
  //     console.error('获取设备信息失败', e)
  //   }
  // }

  // 综合判断
  return isBigScreen || hasTabletInUA
}

/**
 * 检查是否为鸿蒙平板
 * @returns {boolean} 是否为鸿蒙平板
 */
export const isHarmonyPad = () => {
  if (!isHarmonyOS()) return false

  const ua = getUserAgent()

  // 1. 屏幕尺寸判断
  const screenWidth = window.screen.width
  const screenHeight = window.screen.height
  const screenDiagonal = Math.sqrt(screenWidth * screenWidth + screenHeight * screenHeight)
  const isBigScreen = screenDiagonal > 1200

  // 2. 检查UA中是否包含Tablet关键字
  const hasTabletInUA = /Tablet|tablet|Pad|pad/i.test(ua)

  return isBigScreen || hasTabletInUA
}

/**
 * 检查是否为平板设备(iPad、安卓平板或鸿蒙平板)
 * @returns {boolean} 是否为平板设备
 */
export const isPad = () => {
  return isIPad() || isAndroidPad() || isHarmonyPad()
}

/**
 * 同步检测设备是否为平板（适用于无法使用异步方法的场景）
 * @returns {boolean} 是否为平板设备
 */
export const isPadSync = () => {
  const ua = getUserAgent()

  // iPad直接检测
  if (ua.indexOf('iPad') > -1) {
    return true
  }

  // iPadOS(iOS 13+)检测
  if (/Macintosh/.test(ua) && window.navigator.maxTouchPoints > 1) {
    return true
  }

  // 鸿蒙平板检测
  if (isHarmonyOS()) {
    const screenWidth = window.screen.width
    const screenHeight = window.screen.height
    const screenDiagonal = Math.sqrt(screenWidth * screenWidth + screenHeight * screenHeight)
    const hasTabletInUA = /Tablet|tablet|Pad|pad/i.test(ua)
    if (screenDiagonal > 1200 || hasTabletInUA) {
      return true
    }
  }

  // 安卓平板检测
  if (ua.indexOf('Android') > -1 || ua.indexOf('Adr') > -1) {
    // 屏幕尺寸判断
    const screenWidth = window.screen.width
    const screenHeight = window.screen.height
    const screenDiagonal = Math.sqrt(screenWidth * screenWidth + screenHeight * screenHeight)

    // UA中包含Tablet关键字
    const hasTabletInUA = ua.indexOf('Tablet') > -1 || ua.indexOf('tablet') > -1

    // 大屏幕或UA中包含Tablet关键字则判断为平板
    return screenDiagonal > 1200 || hasTabletInUA
  }

  return false
}

/**
 * 检查是否为PC设备
 * @returns {boolean} 是否为PC设备
 */
export const isPC = () => {
  const userAgentInfo = getUserAgent()
  const agents = [
    'HarmonyOS',
    'OpenHarmony',
    'HONOR',
    'Android',
    'iPhone',
    'SymbianOS',
    'Windows Phone',
    'iPad',
    'iPod'
  ]
  let flag = true
  for (let v = 0; v < agents.length; v++) {
    if (userAgentInfo.indexOf(agents[v]) > 0) {
      flag = false
      break
    }
  }
  console.log('isPC flag: ', flag)
  return flag
}
